import { useState, useEffect, useRef } from "react";
import museumData from "../../../SalzzeitOverlay/src/museumData/msrRoomText.json";
import museumDataTemp from "../../../SalzzeitOverlay/src/museumData/tempMuseum.json";
import galleryTextDe from "../EntryRoom/text/imageGalleryText.json";
import galleryTextEn from "../EntryRoom/text/imageGalleryText_en.json";
import ImageSlider from "./ImageSlider";
import MapPoints from "./MapPoints";
import { MDRVideoPoints } from "../Themes/MuseenDerRegion/states/stateMDRVideostation.jsx";
import { MdArrowBackIos } from "react-icons/md";
import ArtefaktBundlesHeimatLandlerMuseum from "./ArtefaktBundles/ArtefaktBundlesHaimatLandlerMuseum";
import ArtefaktBundlesHolzknechtMuseum from "./ArtefaktBundles/ArtefaktBundlesHolzknechtMuseum";
import ArtefaktBundlesKHofBadAusee from "./ArtefaktBundles/ArtefaktBundlesKHofBadAusee";
import ArtefaktBundlesKHofGmunden from "./ArtefaktBundles/ArtefaktBundlesKHofGmunden";
import ArtefaktBundlesMuseumEbensee from "./ArtefaktBundles/ArtefaktBundlesMuseumEbensee";
import ArtefaktBundlesMuseumStadtBadIschl from "./ArtefaktBundles/ArtefaktBundlesMuseumStadtBadIschl";
import ArtefaktBundlesNhm from "./ArtefaktBundles/ArtefaktBundlesNhm";
import ArtefaktBundlesPapiermacher from "./ArtefaktBundles/ArtefaktBundlesPapiermacher";
import ArtefaktBundlesSalzW from "./ArtefaktBundles/ArtefaktBundlesSalzW";
import ArtefaktBundlesWeltErbeMuseumHallstatt from "./ArtefaktBundles/ArtefaktBundlesWeltErbeMuseumHallstatt";
import {
  BsChevronCompactRight,
  BsChevronBarExpand,
} from "react-icons/bs";
import { TbChevronsUpRight } from "react-icons/tb";
import { Canvas } from "@react-three/fiber";
import { useSnapshot } from "valtio";
import { stateArtefactTexts, stateDevice, stateEnglisch, stateNavigation } from "../store";

const MUSEUM_GALLERY_ID = {
  SalHal:   "SalzweltenHallstatt",
  WelHal:   "WelterbemusseumHallstatt",
  AussNhm:  "AussenstelleNHMWien",
  KamBAus:  "KammerhofmuseumBadAussee",
  SalHol:   "SalzkammergutHolzknechtmuseum",
  HeiLan:   "HeimatundLandlermuseum",
  MusBischl:"MuseumStadtBadIschl",
  KaiIschl: "KaiserparkBadIschl",
  MuEbe:    "EbenseeMuseum",
  KzStEb:   "KZGedenkstätteEbensee",
  ZeiEbe:   "ZeitgeschichtemuseumEbensee",
  KhoGmu:   "KHofKammerhofmuseumGmunden",
  SchPau:   "OsterrPapiermacherundDruckereimuseum",
};

function getMuseumsByOrt(ort) {
  return museumData.filter((item) => item.Ort === ort);
}

// Fixed map-size stages for landscape MDR, picked by viewport height (the
// dimension that was clipping the map on mid-size screens). Deliberately
// stepped, not fluid — the dots are %-anchored so they scale safely either
// way, but discrete stages keep the layout from shifting around on resize.
// 520 needs ~830px of height (header + map + Ort buttons + back button).
function mdrMapWidthForViewport(h) {
  if (h >= 830) return 520; // large desktop — unchanged original size
  if (h >= 700) return 430; // laptop / small desktop stage
  return 350;               // tablet-landscape stage
}

export default function SalzzeitMuseen({ onClose, mdrMode }) {
  const transparent = mdrMode;
  const snapDevice = useSnapshot(stateDevice);
  // Desktop MDR (landscape): special compact layout. Portrait MDR: same as sidebar.
  const isDesktopMdr = mdrMode && !snapDevice.isPortrait;
  // Small landscape screens (handy landscape) in MDR: side-by-side layout —
  // content on the left, button column on the right.
  const isCompactLandscapeMdr = isDesktopMdr && snapDevice.isHandy;
  // Sidebar variant (no mdrMode) on handy landscape: the sidebar panel is only
  // ~200-280px wide there — map must fit that width, buttons/texts go compact.
  const snapNav = useSnapshot(stateNavigation);
  const isSidebarLandscapeHandy = !mdrMode && !snapDevice.isPortrait && snapDevice.isHandy;
  const [viewportH, setViewportH] = useState(() => window.innerHeight);
  useEffect(() => {
    const update = () => setViewportH(window.innerHeight);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  // Map in der Landscape-Handy-Sidebar: von der Panelbreite UND der
  // sichtbaren Höhe begrenzt (Header + Ort-Buttons + Zurück brauchen ~200px;
  // ohne den Höhen-Deckel musste man auf flachen Handys zur Map scrollen).
  // Nur hier verwendet — Portrait (355) und MDR (mdrMapWidth) eigene Pfade.
  const sidebarMapWidth = Math.max(140, Math.min((snapNav.sidebarWidth || 280) - 40, viewportH - 200));
  // Compact button padding/text for the narrow landscape-handy sidebar.
  const btnPad = isSidebarLandscapeHandy ? "p-0.5 px-2 text-[11px]" : "p-1.5 px-4 text-sm";
  // Compact landscape: map is height-limited (sits beside the buttons, so the
  // full viewport height minus header/margins caps it). Otherwise: stages.
  const mdrMapWidth = isCompactLandscapeMdr
    ? Math.max(200, Math.min(320, viewportH - 120))
    : mdrMapWidthForViewport(viewportH);
  // Video der ehemaligen 3D-Videostationen im MDR-Raum — jetzt als Buttons
  // in der Hauptansicht; öffnet ein iframe genau wie der "Video"-Unterpunkt.
  const [stationVideo, setStationVideo] = useState(null);
  const btnWrap = isCompactLandscapeMdr
    ? "shrink-0 w-52 px-2 py-2 flex flex-col gap-1 overflow-y-auto justify-center"
    : isDesktopMdr
    ? "shrink-0 px-4 pb-4 pt-2 flex flex-col items-center gap-2"
    : isSidebarLandscapeHandy
    ? "shrink-0 px-2 pb-2 pt-1 flex flex-col space-y-1"
    : "shrink-0 px-2 pb-3 pt-2 flex flex-col space-y-2";
  const btnW = isCompactLandscapeMdr ? "w-full" : isDesktopMdr ? "w-1/3" : "w-full";
  const [currentMuseumId, setCurrentMuseumId] = useState("SalHal");
  const [currentGalerie, setCurrentGalerie] = useState([]);
  const [currentVideo, setCurrentVideo] = useState("");
  const [currentMap, setCurrentMap] = useState("");
  const [currentPano, setCurrentPano] = useState("");
  const [currentTitel, setCurrentTitel] = useState("");
  const [currentOrt, setCurrentOrt] = useState("");
  const [currentText, setCurrentText] = useState([]);
  const [currentUrl, setCurrentUrl] = useState("");
  const [currentUrlTitel, setCurrentUrlTitel] = useState("");
  const [currentModelArrayEmpty, setCurrentModelArrayEmpty] = useState(false);
  const [active, setActive] = useState("Logo");
  const [museumOrtArray, setmuseumOrtArray] = useState([]);

  const snapArtefaktTexts = useSnapshot(stateArtefactTexts);
  const snapEnglisch = useSnapshot(stateEnglisch);
  const isEn = snapEnglisch.isEnglisch;
  const iframeRef = useRef(null);

  useEffect(() => {
    const findObjectById = (id) =>
      museumData.find((item) => item.Id === id) || null;

    const museumCurrentObject = findObjectById(currentMuseumId);
    if (museumCurrentObject) {
      const en = stateEnglisch.isEnglisch;
      const allPics = museumCurrentObject.Images.map((img) => img.Pic);

      const galleryArchiv = en ? galleryTextEn : galleryTextDe;
      const galleryId = MUSEUM_GALLERY_ID[currentMuseumId];
      const galleryEntry = galleryId ? galleryArchiv.find((g) => g.Id === galleryId) : null;
      const allTexts = galleryEntry
        ? galleryEntry.Images.map((img) => img.Text)
        : museumCurrentObject.Images.map((img) => img.Text);

      setCurrentGalerie(allPics);
      setCurrentText(allTexts);
      setCurrentVideo(en ? museumCurrentObject.Museum_VideoEn : museumCurrentObject.Museum_Video);
      setCurrentMap(museumCurrentObject.Museum_Map);
      setCurrentPano(museumCurrentObject.Museum_Pano);
      setCurrentOrt(museumCurrentObject.Ort);
      if (active === "HomepageMuseum") {
        setCurrentTitel("www.salzzeit.at");
        setCurrentUrlTitel("https://salzzeit.at/");
      } else {
        setCurrentTitel(en ? museumCurrentObject.MuseumEn : museumCurrentObject.Museum);
        setCurrentUrl(museumCurrentObject.Link);
        setCurrentUrlTitel(museumCurrentObject.LinkTitel);
      }
    }
    if (
      currentMuseumId === "ZeiEbe" ||
      currentMuseumId === "KzStEb" ||
      currentMuseumId === "KaiIschl"
    ) {
      setCurrentModelArrayEmpty(true);
    } else {
      setCurrentModelArrayEmpty(false);
    }
  }, [currentMuseumId, isEn]);

  useEffect(() => {
    setmuseumOrtArray(getMuseumsByOrt(currentOrt));
  }, [currentOrt]);

  const handleToggle = (name) => {
    setActive(name);
    if (active === "MuseumTemp") {
      setActive("Logo");
    }
  };

  const handleBack = (name) => {
    if (active !== "Artefakte") {
      setActive(name);
      if (active === "Logo") {
        setCurrentMuseumId("");
        setCurrentGalerie([]);
      }
    } else {
      setActive("Galerie");
    }
  };

  const handleMuseumButton = (name) => {
    setActive("Galerie");
    setCurrentMuseumId(name);
  };

  const handleMuseumTempButton = (museum) => {
    if (museum) {
      window.open(museum, "_blank", "noopener,noreferrer");
    }
  };

  const handleHomepageButton = (url) => {
    if (url && active === "Homepage") {
      window.open(url, "_blank", "noopener,noreferrer");
    } else if (active === "HomepageMuseum") {
      window.open("https://salzzeit.at/", "_blank", "noopener,noreferrer");
    }
  };

  const handleDoubleClick = () => {
    const iframe = iframeRef.current;
    if (iframe?.requestFullscreen) iframe.requestFullscreen();
    else if (iframe?.webkitRequestFullscreen) iframe.webkitRequestFullscreen();
  };

  const header = (
    /* Landscape-Handy-Sidebar: deutlich kleinere Titel + engere Abstände —
       jede Headerzeile kostet dort sichtbare Höhe. MDR/Portrait unverändert. */
    <div className={`flex items-center justify-center w-full px-4 relative shrink-0 ${isSidebarLandscapeHandy ? "pb-1 pt-1" : "pb-3 pt-2"}`}>
      <div className="flex flex-col absolute left-2 space-y-2">
        {(active === "Logo" || active === "MuseumTemp") && (
          <button
            onClick={() => handleToggle("MuseumTemp")}
            className={`flex items-center justify-center bg-zinc-800 rounded-full text-white hover:bg-white/10 ${isSidebarLandscapeHandy ? "w-7 h-7" : "w-9 h-9"}`}
          >
            <BsChevronBarExpand size={isSidebarLandscapeHandy ? 18 : 25} color={active === "MuseumTemp" ? "#fc8207" : "gray"} />
          </button>
        )}
      </div>
      {active !== "Logo" && active !== "MuseumTemp" ? (
        <h2 className={`pt-1 mx-12 uppercase font-roboto font-thin tracking-wide text-center text-white ${isSidebarLandscapeHandy ? "text-xs" : "text-l"}`}>
          {currentTitel}
        </h2>
      ) : (
        <h2 className={`font-roboto font-thin uppercase tracking-wide text-center text-white ${isSidebarLandscapeHandy ? "text-xs" : "text-xl"}`}>
          {active === "MuseumTemp"
            ? (isEn ? "Regional Museums" : "Regionale Museen")
            : (isEn ? "The Salzzeit Museums" : "Die Salzzeitmuseen")}
        </h2>
      )}
    </div>
  );

  /* ── STATION VIDEO VIEW: video of a former 3D videostation, same iframe
        pattern as the "Video" submenu item ── */
  if (active === "StationVideo" && stationVideo) {
    return (
      <div className={`absolute inset-0 z-10 flex flex-col ${transparent ? "bg-zinc-900/92" : "bg-zinc-800"}`}>
        <div className="flex items-center justify-center w-full pb-3 pt-2 px-4 relative shrink-0">
          <h2 className="pt-1 mx-12 uppercase font-roboto font-thin tracking-wide text-l text-center text-white">
            {isEn ? stationVideo.TitelEn : stationVideo.Titel}
          </h2>
        </div>
        <div className={`flex-1 min-h-0 flex ${isCompactLandscapeMdr ? "flex-row" : "flex-col"}`}>
          <div className={isCompactLandscapeMdr ? "flex-1 min-w-0 min-h-0 px-2 py-1" : `flex-1 ${isDesktopMdr ? "overflow-hidden flex flex-col w-2/3 mx-auto py-4" : "overflow-y-auto px-2"}`}>
            <iframe allowFullScreen src={isEn ? stationVideo.UrlEn : stationVideo.Url} className={`w-full rounded-xl ${isCompactLandscapeMdr ? "h-full" : isDesktopMdr ? "aspect-video" : "h-[40vh]"}`} />
          </div>
          <div className={btnWrap}>
            <button
              className={`${btnW} font-roboto font-light p-1.5 px-4 text-sm text-center text-white bg-zinc-800 border border-zinc-600 hover:border-lime-500`}
              onClick={() => { setStationVideo(null); setActive("Logo"); }}
            >
              {isEn ? "← Back" : "← Zurück"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── LOGO VIEW: map flex-1, buttons pinned at bottom (compact landscape:
        map left, button column right) ── */
  if (active === "Logo") {
    return (
      <div className={`absolute inset-0 z-10 flex flex-col ${transparent ? "bg-zinc-900/92" : "bg-zinc-800"}`}>
        {header}
        <div className={`flex-1 min-h-0 flex ${isCompactLandscapeMdr ? "flex-row" : "flex-col"}`}>
          <div className={isCompactLandscapeMdr
            ? "flex-1 min-w-0 overflow-hidden flex flex-col items-center justify-center"
            : `flex-1 overflow-y-auto overflow-x-hidden flex flex-col items-center ${isDesktopMdr ? "pt-8" : ""}`}>
            <MapPoints setCurrentOrt={setCurrentOrt} currentOrt={currentOrt} mapWidth={isDesktopMdr ? mdrMapWidth : isSidebarLandscapeHandy ? sidebarMapWidth : 355} />
          </div>
          {/* "contents" keeps the stacked layout byte-identical outside compact
              landscape — the blocks stay direct children of the outer column. */}
          <div className={isCompactLandscapeMdr ? "shrink-0 w-56 px-2 py-2 flex flex-col gap-1 overflow-y-auto justify-center" : "contents"}>
            {museumOrtArray.length > 0 && (
              <div className={isCompactLandscapeMdr
                ? "shrink-0 flex flex-col gap-1"
                : isDesktopMdr ? "shrink-0 px-4 pb-2 pt-2 flex flex-col items-center gap-2"
                : isSidebarLandscapeHandy ? "shrink-0 px-2 pb-2 pt-1 flex flex-col space-y-1"
                : "shrink-0 px-2 pb-3 pt-2 flex flex-col space-y-[1vh]"}>
                {museumOrtArray.map((museum) => (
                  <button
                    key={museum.Id}
                    className={`${btnW} ${btnPad} text-center font-roboto font-light text-white bg-zinc-800 border border-zinc-600 hover:border-lime-500`}
                    onClick={() => handleMuseumButton(museum.Id)}
                  >
                    {isEn ? museum.MuseumEn : museum.Museum}
                  </button>
                ))}
              </div>
            )}
            {/* Videos der ehemaligen Videostationen — nebeneinander, über dem Zurück-Button.
                NUR im MDR-Overlay (mdrMode), nicht in der Sidebar-Version.
                Kein eigenes py: die Nachbar-Container (Ort-Buttons pb / Zurück pt) liefern
                bereits den normalen Button-Abstand. break-words, damit lange Einzelwörter
                (Welterberegion) im Button umbrechen statt überzustehen. */}
            {mdrMode && (
              <div className={isCompactLandscapeMdr
                ? "shrink-0"
                : isDesktopMdr ? "shrink-0 px-4 pt-0 pb-0 flex justify-center" : "shrink-0 px-2 pt-0 pb-0"}>
                <div className={isCompactLandscapeMdr ? "grid grid-cols-2 gap-1 w-full" : `${btnW} flex gap-1`}>
                  {MDRVideoPoints.map((video) => (
                    <button
                      key={video.Id}
                      className="flex-1 min-w-0 font-roboto font-light p-1.5 px-1 text-xs leading-tight text-center text-white bg-zinc-800 border border-zinc-600 hover:border-lime-500 break-words"
                      onClick={() => { setStationVideo(video); setActive("StationVideo"); }}
                    >
                      {isEn ? video.TitelEn : video.Titel}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className={isCompactLandscapeMdr
              ? "shrink-0"
              : isDesktopMdr ? "shrink-0 px-4 pb-4 pt-2 flex justify-center"
              : isSidebarLandscapeHandy ? "shrink-0 px-2 pb-2 pt-1"
              : "shrink-0 px-2 pb-3 pt-2"}>
              <button
                className={`${btnW} font-roboto font-light ${btnPad} text-center text-white bg-zinc-800 border border-zinc-600 hover:border-lime-500`}
                onClick={onClose}
              >
                {isEn ? "← Back" : "← Zurück"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── MUSEUM TEMP VIEW: scrollable grid ── */
  if (active === "MuseumTemp") {
    return (
      <div className={`absolute inset-0 z-10 flex flex-col ${transparent ? "bg-zinc-900/92" : "bg-zinc-800"}`}>
        {header}
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          <div className="grid grid-cols-1 gap-2">
            {museumDataTemp.map((museumTemp) => (
              <button
                key={museumTemp.Id}
                className="p-1.5 px-2 text-center font-roboto font-light text-sm text-white bg-zinc-800 border border-zinc-600 hover:border-lime-500"
                onClick={() => handleMuseumTempButton(museumTemp.iFrameUrl)}
              >
                {museumTemp.Titel}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── ARTEFAKTE VIEW ── */
  if (active === "Artefakte") {
    const canvasContent = (
      <>
        {currentMuseumId === "HeiLan" && <ArtefaktBundlesHeimatLandlerMuseum currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "SalHol" && <ArtefaktBundlesHolzknechtMuseum currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "KamBAus" && <ArtefaktBundlesKHofBadAusee currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "KhoGmu" && <ArtefaktBundlesKHofGmunden currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "MuEbe" && <ArtefaktBundlesMuseumEbensee currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "MusBischl" && <ArtefaktBundlesMuseumStadtBadIschl currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "AussNhm" && <ArtefaktBundlesNhm currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "SchPau" && <ArtefaktBundlesPapiermacher currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "SalHal" && <ArtefaktBundlesSalzW currentMuseumId={currentMuseumId} />}
        {currentMuseumId === "WelHal" && <ArtefaktBundlesWeltErbeMuseumHallstatt currentMuseumId={currentMuseumId} />}
      </>
    );
    return (
      <div className={`absolute inset-0 z-10 flex flex-col ${transparent ? "bg-zinc-900/92" : "bg-zinc-800"}`}>
        {header}
        {isDesktopMdr ? (
          <div className={`flex-1 min-h-0 flex ${isCompactLandscapeMdr ? "flex-row" : "flex-col"}`}>
            <div className={isCompactLandscapeMdr
              ? "flex-1 min-w-0 min-h-0 flex flex-col px-2 py-1"
              : "flex-1 min-h-0 flex flex-col w-2/3 mx-auto py-4"}>
              <Canvas className="flex-1 min-h-0 w-full rounded-xl bg-zinc-900">
                {canvasContent}
              </Canvas>
              <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 100, fontSize: '14px', letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'center', color: 'white', paddingTop: '8px', width: '100%' }}>
                {snapArtefaktTexts.Titel}
              </p>
              <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 300, fontSize: '12px', lineHeight: '1.25rem', letterSpacing: '0.025em', textAlign: 'center', color: 'white', padding: '4px 0 0', width: '100%' }}>
                {snapArtefaktTexts.Text}
              </p>
            </div>
            <div className={isCompactLandscapeMdr ? btnWrap : "shrink-0 px-4 pb-4 pt-2 flex justify-center"}>
              <button
                className={`${btnW} font-roboto font-light p-1.5 px-4 text-sm text-center text-white bg-zinc-800 border border-zinc-600 hover:border-lime-500`}
                onClick={() => handleBack("Galerie")}
              >
                {isEn ? "← Back" : "← Zurück"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="h-[50vh] shrink-0">
              <Canvas className="w-full h-full rounded-xl bg-zinc-900">
                {canvasContent}
              </Canvas>
            </div>
            <div className="flex-1 overflow-y-auto flex flex-col items-center px-3 mt-2 space-y-1">
              <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 100, fontSize: '14px', letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'center', color: 'white', width: '100%' }}>
                {snapArtefaktTexts.Titel}
              </p>
              <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 300, fontSize: '12px', lineHeight: '1.25rem', letterSpacing: '0.025em', textAlign: 'center', color: 'white', padding: '4px 0 0', width: '100%' }}>
                {snapArtefaktTexts.Text}
              </p>
            </div>
            <div className="shrink-0 px-2 pb-3 pt-2">
              <button
                className={`w-full font-roboto font-light ${btnPad} text-center text-white bg-zinc-800 border border-zinc-600 hover:border-lime-500`}
                onClick={() => handleBack("Galerie")}
              >
                {isEn ? "← Back" : "← Zurück"}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ── DETAIL VIEWS: Galerie / Video / Map / Panorama / Homepage (compact
        landscape: content left, button column right) ── */
  return (
    <div className={`absolute inset-0 z-10 flex flex-col ${transparent ? "bg-zinc-900/92" : "bg-zinc-800"}`}>
      {header}
      <div className={`flex-1 min-h-0 flex ${isCompactLandscapeMdr ? "flex-row" : "flex-col"}`}>
      <div className={isCompactLandscapeMdr
        ? "flex-1 min-w-0 min-h-0 px-2 py-1 overflow-hidden flex flex-col"
        : `flex-1 ${isDesktopMdr ? "overflow-hidden flex flex-col w-2/3 mx-auto py-4" : "overflow-y-auto px-2"}`}>
        {active === "Galerie" && (
          <ImageSlider currentGalerie={currentGalerie} currentText={currentText} mdrMode={isDesktopMdr} portraitMdr={mdrMode && !isDesktopMdr} compactSidebar={isSidebarLandscapeHandy} />
        )}
        {active === "Video" && (
          <iframe allowFullScreen src={currentVideo} className={`w-full rounded-xl ${isCompactLandscapeMdr ? "h-full" : isDesktopMdr ? "aspect-video" : "h-[40vh]"}`} />
        )}
        {active === "Map" && (
          <iframe ref={iframeRef} onDoubleClick={handleDoubleClick} allowFullScreen src={currentMap} className={`w-full rounded-xl ${isCompactLandscapeMdr ? "h-full" : isDesktopMdr ? "aspect-video" : "h-[47vh]"}`} />
        )}
        {active === "Panorama" && (
          <iframe allowFullScreen src={currentPano} className={`w-full rounded-xl ${isCompactLandscapeMdr ? "h-full" : isDesktopMdr ? "aspect-video" : "h-[47vh]"}`} />
        )}
        {(active === "Homepage" || active === "HomepageMuseum") && (
          <div onClick={() => handleHomepageButton(currentUrl)} className={`flex flex-col items-center justify-center cursor-pointer ${isCompactLandscapeMdr ? "h-full" : "h-[40vh]"}`}>
            <TbChevronsUpRight size={150} color={active === "HomepageMuseum" ? "#fc8207" : "gray"} className="animate-pulse" />
            {/* Inline styles: InfoWindowStory.css defines a global bare `p` rule
                (align-self: flex-start, text-align: left) that beats Tailwind's
                layered utilities — inline wins over both. */}
            <p className="font-roboto font-light px-3 text-ms leading-5 tracking-wide text-white" style={{ textAlign: "center", alignSelf: "center" }}>
              {active === "Homepage" ? currentUrlTitel : "www.salzzeit.at"}
            </p>
          </div>
        )}
      </div>
      {/* Buttons pinned at bottom (compact landscape: right column) */}
      <div className={btnWrap}>
        <button className={`${btnW} font-roboto font-light ${btnPad} text-center text-white bg-zinc-800 border hover:border-lime-500 ${active === "Galerie" ? "border-lime-500" : "border-zinc-600"}`} onClick={() => handleToggle("Galerie")}>{isEn ? "Gallery" : "Galerie"}</button>
        <button className={`${btnW} font-roboto font-light ${btnPad} text-center text-white bg-zinc-800 border hover:border-lime-500 ${active === "Video" ? "border-lime-500" : "border-zinc-600"}`} onClick={() => handleToggle("Video")}>Video</button>
        <button className={`${btnW} font-roboto font-light ${btnPad} text-center text-white bg-zinc-800 border hover:border-lime-500 ${active === "Map" ? "border-lime-500" : "border-zinc-600"}`} onClick={() => handleToggle("Map")}>Map</button>
        <button className={`${btnW} font-roboto font-light ${btnPad} text-center text-white bg-zinc-800 border hover:border-lime-500 ${active === "Panorama" ? "border-lime-500" : "border-zinc-600"}`} onClick={() => handleToggle("Panorama")}>Panorama</button>
        <div className={`flex gap-1 ${btnW}`}>
          <button className={`flex-1 font-roboto font-light ${isSidebarLandscapeHandy ? "p-0.5 text-[11px]" : "p-1.5 text-sm"} text-center text-white bg-zinc-800 border hover:border-lime-500 ${active === "Homepage" ? "border-lime-500" : "border-zinc-600"}`} onClick={() => handleToggle("Homepage")}>Homepage</button>
          {!currentModelArrayEmpty && (
            <button className={`flex-1 font-roboto font-light ${isSidebarLandscapeHandy ? "p-0.5 text-[11px]" : "p-1.5 text-sm"} text-center text-white bg-zinc-800 border hover:border-lime-500 ${active === "Artefakte" ? "border-lime-500" : "border-zinc-600"}`} onClick={() => handleToggle("Artefakte")}>{isEn ? "3D Artefacts" : "3D Artefakte"}</button>
          )}
        </div>
        <button
          className={`${btnW} font-roboto font-light ${btnPad} text-center text-white bg-zinc-800 border border-zinc-600 hover:border-lime-500`}
          onClick={() => handleBack("Logo")}
        >
          {isEn ? "← Back" : "← Zurück"}
        </button>
      </div>
      </div>
    </div>
  );
}
